import { parsePhoneNumberFromString } from "libphonenumber-js";
import { FIELD_REFUSAL_PATTERN, sentence } from "./refusal";

/**
 * What the phone-number dialogs check before they send anything: every rule
 * of the `phone-number` function (supabase/functions/phone-number/) and, for
 * the make-a-call dialog, of the `call` function (supabase/functions/call/),
 * so a save or a call the server would refuse never leaves the dialog and the
 * reason shows under the field that needs changing, in words rather than as
 * the server's `field: message`.
 */

/** A field of the phone-number dialogs that can be wrong. */
export type PhoneNumberField =
  | "phoneNumber"
  | "nickname"
  | "twilioSid"
  | "authToken"
  | "terminationUri"
  | "sipUsername"
  | "sipPassword"
  | "inboundAgentId"
  | "outboundAgentId"
  | "maxCps"
  | "inboundWebhookUrl"
  | "toNumber"
  | "fromNumber"
  | "agentId";

/** Each field's error; a field that is fine has none. */
export type PhoneNumberErrors = Partial<Record<PhoneNumberField, string>>;

/**
 * The number as the server stores it, or null when its rule refuses it: a `+`,
 * then a number libphonenumber-js's default metadata holds valid. `+1555…` is
 * refused — 555 is not a North American area code.
 */
export function toE164(value: string): string | null {
  const text = value.trim();
  const parsed = text.startsWith("+") ? parsePhoneNumberFromString(text) : undefined;
  return parsed?.isValid() ? parsed.format("E.164") : null;
}

function phoneNumberError(value: string): string | undefined {
  if (!value.trim()) return "Enter the phone number.";
  return toE164(value)
    ? undefined
    : "Enter a valid number in E.164 format: + and the country code, e.g. +14155550123.";
}

const nicknameError = (nickname: string) =>
  nickname.trim().length > 100 ? "Keep the nickname to 100 characters or fewer." : undefined;

export function twilioNumberErrors(form: {
  phoneNumber: string;
  nickname: string;
  twilioSid: string;
  authToken: string;
}): PhoneNumberErrors {
  const sid = form.twilioSid.trim();
  const token = form.authToken.trim();
  return {
    phoneNumber: phoneNumberError(form.phoneNumber),
    twilioSid: !sid
      ? "Enter your Twilio Account SID."
      : /^AC[0-9a-f]{32}$/i.test(sid)
        ? undefined
        : "A Twilio Account SID starts with AC followed by 32 hex characters.",
    authToken: !token
      ? "Enter your Twilio Auth Token."
      : /\s/.test(token)
        ? "An Auth Token has no spaces in it."
        : token.length > 128
          ? "An Auth Token is at most 128 characters."
          : undefined,
    nickname: nicknameError(form.nickname),
  };
}

/** A hostname or IPv4 address, optionally with `:port`, and no scheme. */
const TERMINATION_URI = /^[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?(?::\d{1,5})?$/;

export function sipNumberErrors(form: {
  phoneNumber: string;
  nickname: string;
  terminationUri: string;
  sipUsername: string;
  sipPassword: string;
}): PhoneNumberErrors {
  const uri = form.terminationUri.trim();
  // As the server reads them: the user name trimmed, the password as typed.
  const username = form.sipUsername.trim();
  const password = form.sipPassword;
  return {
    phoneNumber: phoneNumberError(form.phoneNumber),
    nickname: nicknameError(form.nickname),
    terminationUri: !uri
      ? "Enter your carrier's termination URI."
      : uri.length > 253
        ? "A termination URI is at most 253 characters."
        : TERMINATION_URI.test(uri)
          ? undefined
          : "Enter a hostname such as example.pstn.twilio.com, optionally with :port, and no sip: or https:// in front.",
    sipUsername:
      username.length > 128
        ? "A user name is at most 128 characters."
        : password && !username
          ? "Enter the user name for this password, or clear the password."
          : undefined,
    sipPassword:
      password.length > 128
        ? "A password is at most 128 characters."
        : username && !password
          ? "Enter the password for this user name, or clear the user name."
          : undefined,
  };
}

function isHttpsUrl(text: string): boolean {
  try {
    return new URL(text).protocol === "https:";
  } catch {
    return false;
  }
}

/** `inboundWebhookUrl` is "" on a Twilio number, which never sends one. */
export function editNumberErrors(form: { maxCps: string; inboundWebhookUrl: string }): PhoneNumberErrors {
  // An empty box reads as 0.
  const cps = Number(form.maxCps);
  const url = form.inboundWebhookUrl.trim();
  return {
    maxCps: Number.isFinite(cps) && cps >= 0.1 ? undefined : "Enter a rate of at least 0.1 calls per second.",
    inboundWebhookUrl: !url
      ? undefined
      : url.length > 2048
        ? "A webhook URL is at most 2048 characters."
        : isHttpsUrl(url)
          ? undefined
          : "Enter an https:// URL, e.g. https://api.example.com/verbeo-inbound.",
  };
}

/** The spaces, dashes and brackets people paste in a number to call. */
const DIAL_SEPARATORS = /[\s()-]/g;

/** What is left once they are gone: E.164, with the `+` optional. */
const DIALLED_NUMBER = /^\+?[1-9]\d{1,14}$/;

/**
 * The number to call as it is checked and sent: without its separators, and
 * with no `+` added — "(201) 234-5678" with one is a number in Egypt.
 */
export const dialledNumber = (value: string) => value.replace(DIAL_SEPARATORS, "");

/**
 * What the database cannot hold, and the `call` function refuses: a NUL, or half
 * of a surrogate pair — an emoji a paste cut in two.
 */
const UNSTORABLE = /\0|\p{Cs}/gu;

/**
 * A variable's value as it is sent: trimmed, and without what cannot be stored.
 * Nobody types that, so it is dropped here rather than refused by the server.
 */
export const sentValue = (value: string) => value.replace(UNSTORABLE, "").trim();

/** A number to call, one of the workspace's numbers to call it from, and an agent. */
export function callErrors(form: { toNumber: string; fromNumber: string; agentId: string }): PhoneNumberErrors {
  return {
    toNumber: !form.toNumber.trim()
      ? "Please enter a phone number to call."
      : DIALLED_NUMBER.test(dialledNumber(form.toNumber))
        ? undefined
        : "Please enter a valid phone number in E.164 format (e.g., +1234567890).",
    fromNumber: form.fromNumber ? undefined : "Please select a phone number to call from.",
    agentId: form.agentId ? undefined : "Please select an agent for this call.",
  };
}

/** A dialog's fields as it sent them. */
type SentForm = Record<string, string>;

/**
 * A refusal from the function as a dialog shows it: under `field` while that
 * field still holds what was `sent`, or for the whole dialog when it has no
 * field of that name.
 */
export interface Refusal {
  field?: PhoneNumberField;
  sent?: string;
  message: string;
}

/** The body fields the server names in a `field: message` refusal, and the dialog field each one is. */
const SERVER_FIELDS: Record<string, PhoneNumberField> = {
  phone_number: "phoneNumber",
  nickname: "nickname",
  twilio_sid: "twilioSid",
  twilio_auth_token: "authToken",
  termination_uri: "terminationUri",
  sip_username: "sipUsername",
  sip_password: "sipPassword",
  inbound_agent_id: "inboundAgentId",
  outbound_agent_id: "outboundAgentId",
  max_cps: "maxCps",
  inbound_webhook_url: "inboundWebhookUrl",
  to_number: "toNumber",
  from_number: "fromNumber",
  agent_id: "agentId",
};

function reasonOf(status: unknown, text: string): { field?: PhoneNumberField; message: string } {
  // A number is unique across Verbeo. Only that conflict belongs under the
  // number; any other 409 is already a sentence for the whole dialog.
  if (status === 409) {
    return text.endsWith(" is already connected to Verbeo") ? { field: "phoneNumber", message: text } : { message: text };
  }
  const match = FIELD_REFUSAL_PATTERN.exec(text);
  if (!match) return { message: text };
  const field = SERVER_FIELDS[match[1]];
  if (field === "inboundAgentId" || field === "outboundAgentId" || field === "agentId") {
    return { field, message: "This agent is not in this workspace any more. Choose another one." };
  }
  // The number to call from was deleted, or switched off, after the list loaded.
  if (field === "fromNumber") {
    return { field, message: "This number is not available any more. Choose another one." };
  }
  return { field, message: sentence(match[2]) };
}

/**
 * A failed request, for `form` — the dialog's fields as it sent them, or `{}`
 * for a button with no fields. What the checks above mirror never gets here;
 * what does is what only the server knows: the number is already connected, an
 * agent or a number picked here has left the workspace, or LiveKit, Twilio, the
 * dialer or the caller's role refused — those are sentences already. A
 * `field: message` string is never shown as it came: it goes under its field,
 * without the column name.
 */
export function refusalOf(error: unknown, form: SentForm, fallback: string): Refusal {
  const { status, message } = (error ?? {}) as { status?: unknown; message?: unknown };
  const reason = reasonOf(status, typeof message === "string" && message ? message : fallback);
  return reason.field && reason.field in form
    ? { ...reason, sent: form[reason.field] }
    : { message: reason.message };
}

/** The field a refusal is about, while it still holds what was sent: changing it clears the error. */
export const refusedFields = (refusal: Refusal | undefined, form: SentForm): PhoneNumberErrors =>
  refusal?.field && form[refusal.field] === refusal.sent ? { [refusal.field]: refusal.message } : {};
